import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { Breakpoint, Logo, useBreakpoint } from '@genially/design-system';
import { PlayP2pClientEventType } from '@genially/p2p-client';

import { PlayersLimitReachedNotification } from '../../../../../../shared/application/components/PlayersLimitReachedNotification/PlayersLimitReachedNotification';
import { useOpenGenially } from '../../../../../../shared/application/hooks/useOpenGenially';
import { Players } from '../../components/Players/Players';
import { useHostClientContext } from '../HostClientProvider/hooks/useHostClientContext';
import { ConnectionInfo } from './components/ConnectionInfo/ConnectionInfo';
import { Header } from './components/Header/Header';
import { ParticipantsInfo } from './components/ParticipantsInfo/ParticipantsInfo';
import { StartButton } from './components/StartButton/StartButton';
import {
  BannerWrapper,
  ContentWrapper,
  HeaderContentWrapper,
  InfoContentWrapper,
  LogoWrapper,
  ParticipantsContentWrapper,
  ParticipantsInfoWrapper,
  PlayersContent,
  StartButtonWrapper,
  Wrapper,
} from './HostLobby.styled';

export const HostLobby = observer(() => {
  const [showAll, setShowAll] = useState(false);

  const [infoContainerCentered, setInfoContainerCentered] = useState(false);
  const [entranceAnimation, setEntranceAnimation] = useState(false);
  const [initialInfoContainerRef, setInitialInfoContainerRef] = useState<{
    top: number;
    left: number;
    height: number;
    width: number;
  } | null>(null);
  const [finalInfoContainerRef, setFinalInfoContainerRef] = useState<{
    top: number;
    left: number;
    height: number;
    width: number;
  } | null>(null);

  const handleLogoClick = useOpenGenially();

  const { isDownwards } = useBreakpoint();

  const isMobile = isDownwards(Breakpoint.xs);

  const { client } = useHostClientContext();

  useEffect(() => {
    client.on(PlayP2pClientEventType.Error, error => {
      console.error(error);
    });
  }, [client]);

  const loadInfoContentRef = (node: HTMLDivElement | null) => {
    if (node && !infoContainerCentered && !finalInfoContainerRef) {
      const { top, left, height, width } = node.getBoundingClientRect();

      setFinalInfoContainerRef({ top, left, height, width });

      setInfoContainerCentered(true);
    } else if (node && infoContainerCentered && !initialInfoContainerRef) {
      const { top, left, height, width } = node.getBoundingClientRect();

      setInitialInfoContainerRef({ top, left, height, width });

      setInfoContainerCentered(false);
      setEntranceAnimation(true);
    }
  };

  const handleInfoContentWrapperAnimationEnd = () => {
    setShowAll(true);
  };

  return (
    <Wrapper>
      <BannerWrapper>
        <PlayersLimitReachedNotification show={client.isPlayersLimitReached} />
      </BannerWrapper>

      <ContentWrapper
        $isInfoCentered={infoContainerCentered}
        $participantsStickyTop={finalInfoContainerRef ? finalInfoContainerRef.height : 0}
      >
        <HeaderContentWrapper $show={showAll} $isMobile={isMobile}>
          <Header />
        </HeaderContentWrapper>

        <InfoContentWrapper
          ref={loadInfoContentRef}
          $show={infoContainerCentered || showAll}
          $centered={infoContainerCentered}
          $hasAnimation={entranceAnimation}
          $initialState={initialInfoContainerRef}
          $finalState={finalInfoContainerRef}
          onAnimationEnd={handleInfoContentWrapperAnimationEnd}
        >
          <ConnectionInfo />
        </InfoContentWrapper>
        <ParticipantsContentWrapper $show={showAll} $isMobile={isMobile}>
          <ParticipantsInfoWrapper>
            <ParticipantsInfo />
          </ParticipantsInfoWrapper>

          <PlayersContent>
            <Players centerAligned />
          </PlayersContent>
        </ParticipantsContentWrapper>
      </ContentWrapper>

      {isMobile ? (
        <StartButtonWrapper $show={showAll}>
          <StartButton />
        </StartButtonWrapper>
      ) : (
        <LogoWrapper onClick={handleLogoClick}>
          <Logo variant={Logo.Variant.LIGHT} hasAnimation />
        </LogoWrapper>
      )}
    </Wrapper>
  );
});
